import React, { useEffect, useState, useRef } from "react";
import { Drawer } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useSelector } from "react-redux";
import moment from "moment-timezone";
import Icon from "./icons";
import { getAllAttendenceStaff, getAllStaff } from "../../../services/staff";
import { getAllPatientsModal } from "../../../services/patient";
import { getAllFacilitiesModal } from "../../../services/facility";
import { addAttendanceStaff } from "../../../services/UserTracking";
import WeekPicker from "./WeekPicker";
import { MON_SHORT } from "./scheduleUtils";
import "./SchedulerCreate.css";

// Single-date field using the Scheduler's calendar design (WeekPicker day mode).
function DateField({ value, onChange, maxDate }) {
  const [open, setOpen] = useState(false);
  const [dpMonth, setDpMonth] = useState(() => { const d = value ? new Date(value) : new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("click", h);
    return () => document.removeEventListener("click", h);
  }, [open]);
  const label = value ? `${MON_SHORT[new Date(value).getMonth()]} ${new Date(value).getDate()}, ${new Date(value).getFullYear()}` : null;
  return (
    <div className="cdate" ref={ref}>
      <button type="button" className="inp cdate-btn" onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}>
        {label ? <span>{label}</span> : <span className="cdd-ph">Select date</span>}
        <Icon name="calendar" size={16} />
      </button>
      {open ? (
        <div className="datepop">
          <WeekPicker mode="day" selDay={value ? new Date(value) : new Date()} monthDate={dpMonth} maxDate={maxDate} onMonthChange={setDpMonth} onPick={(d) => { onChange(d); setOpen(false); }} onClose={() => setOpen(false)} />
        </div>
      ) : null}
    </div>
  );
}

// Styled single-select dropdown — same look as the rest of the Scheduler (cdd).
function SelectDD({ placeholder, value, options, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("click", h);
    return () => document.removeEventListener("click", h);
  }, [open]);
  const sel = options.find((o) => o.value === value);
  return (
    <div className={`cdd ${open ? "open" : ""}`} ref={ref}>
      <button type="button" className="cdd-btn" onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}>
        {sel ? <span>{sel.label}</span> : <span className="cdd-ph">{placeholder}</span>}
        <Icon name="chevronDown" size={16} />
      </button>
      <div className="cdd-menu">
        {options.map((o) => (
          <div key={o.value || "__empty"} className={`cdd-opt ${o.value === value ? "sel" : ""}`} onClick={() => { onChange(o.value); setOpen(false); }}>
            <div className="co-name">{o.label}</div>
            <span className="co-chk"><Icon name="check" size={15} /></span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Multi-select services dropdown — matches the create-shift drawer.
function ServiceMultiSelect({ options, value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("click", h);
    return () => document.removeEventListener("click", h);
  }, [open]);
  const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  const selected = options.filter((o) => value.includes(o.value));
  return (
    <div className="multiselect" ref={ref}>
      <div className={`ms-control ${open ? "open" : ""}`} onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}>
        {selected.length === 0 ? (
          <span className="ms-placeholder">Select services...</span>
        ) : (
          selected.map((o) => (
            <span className="pill" key={o.value}>
              <span>{o.label}</span>
              <button type="button" className="pill-x" onClick={(e) => { e.stopPropagation(); toggle(o.value); }}>&times;</button>
            </span>
          ))
        )}
        <span className="ms-arrow"><Icon name="chevronDown" size={16} /></span>
      </div>
      <div className={`ms-dropdown ${open ? "open" : ""}`}>
        <div className="ms-list">
          {options.map((o) => (
            <label key={o.value} className={`ms-option ${value.includes(o.value) ? "selected" : ""}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggle(o.value); }}>
              <span className="ms-checkbox"><Icon name="check" size={13} /></span>
              <span className="ms-name">{o.label}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

// Scheduler-native "Add Missed Time" drawer — same staff-aware facility/client/
// service + break logic as the legacy AddAttendanceModal, rendered in the sd-*
// side-panel design used across the Scheduler.
export default function MissedShiftDrawer({ opened, onClose, staffId = "all", currentPage, range, onSaved, prefill = null }) {
  const [loading, setLoading] = useState(false);

  const allStaff = useSelector((state) => state.staff?.allStaff) || [];
  const allPatients = useSelector((state) => state.patient?.allPatients) || [];
  const facilityState = useSelector((state) => state.facility);
  const allFacilities = facilityState?.allFacilities?.facilities || facilityState?.allDeletedFacilities?.facilities || [];
  // The logged-in admin/owner can also clock in (they aren't a staff record and
  // have no assigned facilities), so include them and grant access to all.
  const { profile } = useSelector((state) => state?.profile) || {};
  const adminUser = profile?.user;
  const adminId = adminUser?.id || adminUser?._id;
  const adminName = adminUser?.name || `${adminUser?.firstName || ""} ${adminUser?.lastName || ""}`.trim() || "Admin";

  useEffect(() => {
    if (!allStaff.length) getAllStaff("all", false, undefined, "");
    if (!allPatients.length) getAllPatientsModal("all", false);
    if (!allFacilities.length) getAllFacilitiesModal("all", false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [type, setType] = useState("client"); // 'client' | 'facility' (Shift Location)

  const form = useForm({
    initialValues: { staffId: staffId !== "all" ? staffId : "", facilityId: "", clientId: "", services: [], date: new Date(), startTime: "", endTime: "", breaks: [], notes: "" },
  });

  useEffect(() => {
    if (opened) {
      form.reset();
      // Prefill from a specific missed shift (staff, location, service, date and
      // scheduled times) so resolving a no-show is a one-click, mostly-filled form.
      if (prefill) {
        setType(prefill.type === "facility" ? "facility" : "client");
        form.setValues({
          staffId: prefill.staffId || (staffId !== "all" ? staffId : ""),
          facilityId: prefill.facilityId || "",
          clientId: prefill.clientId || "",
          services: prefill.services || [],
          date: prefill.date || new Date(),
          startTime: prefill.startTime || "",
          endTime: prefill.endTime || "",
          breaks: [],
          notes: "",
        });
      } else {
        setType("client");
        if (staffId !== "all") form.setFieldValue("staffId", staffId);
      }
    }
  }, [opened]); // eslint-disable-line react-hooks/exhaustive-deps

  // Options are independent of the selected staff (mirrors the Add Schedule
  // drawer) — but unlike a schedule, a time entry records hours someone
  // actually worked, so there is no "Unassigned" choice here.
  const staffOptions = [
    ...(adminId ? [{ value: adminId, label: `${adminName} (Admin)` }] : []),
    ...(allStaff
      ?.filter((s) => s?.active === true && s?.deleted === false && s?.type !== "contractor")
      ?.map((s) => ({ value: s._id || s.id, label: `${s.firstName || ""} ${s.lastName || ""}`.trim() || s.name || s._id })) || []),
  ];
  const facilityOptions = allFacilities
    ?.filter((f) => f?.active === true && f?.deleted === false)
    ?.map((f) => ({ value: f?.id || f?._id, label: f?.name })) || [];
  const clientOptions = allPatients
    ?.filter((p) => p?.active === true && p?.deleted === false)
    ?.map((p) => ({ value: p?.id || p?._id, label: p?.name || `${p?.form1?.firstName || ""} ${p?.form1?.lastName || ""}`.trim() })) || [];
  const selectedClientObj = allPatients?.find((p) => (p?.id || p?._id) === form.values.clientId);
  const serviceOptions = selectedClientObj?.form1?.services?.map((ser) => ({ label: ser?.servicetitle, value: ser?.servicenumber })) || [];

  const addBreak = () => form.insertListItem("breaks", { breakStart: "", breakEnd: "" });
  const removeBreak = (i) => form.removeListItem("breaks", i);

  const handleSubmit = async () => {
    const { staffId: sId, facilityId, clientId, services, date, startTime, endTime, breaks, notes } = form.values;
    let hasError = false;
    if (!sId) { form.setFieldError("staffId", "Staff member is required"); hasError = true; }
    if (!facilityId) { form.setFieldError("facilityId", "Facility is required"); hasError = true; }
    if (type === "client" && !clientId) { form.setFieldError("clientId", "Client is required"); hasError = true; }
    if (!date) { form.setFieldError("date", "Date is required"); hasError = true; }
    if (!startTime) { form.setFieldError("startTime", "Clock-in time is required"); hasError = true; }
    if (!endTime) { form.setFieldError("endTime", "Clock-out time is required"); hasError = true; }
    if (hasError) return;

    try {
      setLoading(true);
      const facObj = allFacilities?.find((f) => (f?.id || f?._id) === facilityId);
      const facilityTz = facObj?.timeZone || null;
      const dateStr = moment(date).format("YYYY-MM-DD");
      const buildDate = (timeStr) => {
        if (facilityTz) return moment.tz(`${dateStr} ${timeStr}`, "YYYY-MM-DD HH:mm", facilityTz).toDate();
        const d = new Date(date);
        const [h, m] = timeStr.split(":").map(Number);
        d.setHours(h, m, 0, 0);
        return d;
      };
      const _date = facilityTz ? moment.tz(dateStr, "YYYY-MM-DD", facilityTz).startOf("day").toDate() : (() => { const d = new Date(date); d.setHours(0, 0, 0, 0); return d; })();
      const _startTime = buildDate(startTime);
      const _endTime = buildDate(endTime);
      if (_endTime <= _startTime) { form.setFieldError("endTime", "Clock-out must be after clock-in"); setLoading(false); return; }
      const breakObjects = breaks.filter((b) => b.breakStart && b.breakEnd).map((b) => ({ breakStart: buildDate(b.breakStart), breakEnd: buildDate(b.breakEnd) }));

      // manualEntry flags the record as hand-keyed rather than clocked, so the
      // attendance table, detail drawers and exports can segregate these hours.
      const payload = { staff: sId, facility: facilityId, checkin_type: type, date: _date, startTime: _startTime, endTime: _endTime, status: "clock-out", breaks: breakObjects, manualEntry: true, manualEntryReason: (notes || "").trim() };
      if (type === "client") { payload.client = clientId; payload.services = services; payload.service = services[0] || null; }

      const result = await addAttendanceStaff(payload);
      if (result) {
        await getAllAttendenceStaff(currentPage, range);
        onSaved?.();
        onClose();
      }
    } finally {
      setLoading(false);
    }
  };

  const duration = (() => {
    const s = form.values.startTime;
    const e = form.values.endTime;
    if (!s || !e) return "—";
    const [sh, sm] = s.split(":").map(Number);
    const [eh, em] = e.split(":").map(Number);
    let mins = eh * 60 + em - (sh * 60 + sm);
    if (mins < 0) mins += 1440;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h}h${m ? ` ${m}m` : ""}`;
  })();

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size={520}
      padding={0}
      withCloseButton={false}
      title={null}
      classNames={{ content: "sdDrawerContent" }}
      styles={{ body: { height: "100%", padding: 0 } }}
      zIndex={1000}
    >
      <div className="sd-root">
        <div className="sd-head">
          <div>
            <h3>Add Missed Time</h3>
          </div>
          <button type="button" className="sd-close" onClick={onClose} aria-label="Close"><Icon name="x" size={16} /></button>
        </div>

        <div className="sd-body sd-create">
          {/* ---------- Assignment ---------- */}
          <div className="cs-section">
            <div className="frow">
              <div className="field" style={{ marginBottom: 0 }}>
                <label>Facility <span className="req">*</span></label>
                <SelectDD placeholder="Select a facility" value={form.values.facilityId} options={facilityOptions} onChange={(val) => form.setFieldValue("facilityId", val)} />
                {form.errors.facilityId ? <div className="evvr-err">{form.errors.facilityId}</div> : null}
              </div>
              <div className="field" style={{ marginBottom: 0 }}>
                <label>Staff member <span className="req">*</span></label>
                <SelectDD placeholder="Select a staff member" value={form.values.staffId} options={staffOptions} onChange={(val) => form.setFieldValue("staffId", val)} />
                {form.errors.staffId ? <div className="evvr-err">{form.errors.staffId}</div> : null}
              </div>
            </div>

            <div style={{ marginTop: 16 }}>
              <div className="type-label">Shift Location</div>
              <div className="type-cards">
                <label>
                  <input type="radio" name="msftype" value="client" checked={type === "client"} onChange={() => { setType("client"); }} />
                  <div className="type-card">
                    <div className="tc-ic"><Icon name="person" size={18} /></div>
                    <div className="tc-t">Client Visit</div>
                    <div className="tc-s">Home or community-based care</div>
                  </div>
                </label>
                <label>
                  <input type="radio" name="msftype" value="facility" checked={type === "facility"} onChange={() => { setType("facility"); form.setFieldValue("clientId", ""); form.setFieldValue("services", []); }} />
                  <div className="type-card">
                    <div className="tc-ic"><Icon name="hospital" size={18} /></div>
                    <div className="tc-t">Facility Shift</div>
                    <div className="tc-s">Residential care facility or clinic</div>
                  </div>
                </label>
              </div>
            </div>

            {type === "client" ? (
              <div style={{ marginTop: 16 }}>
                <div className="field">
                  <label>Client <span className="req">*</span></label>
                  <SelectDD placeholder="Select a client" value={form.values.clientId} options={clientOptions} onChange={(val) => { form.setFieldValue("clientId", val); form.setFieldValue("services", []); }} />
                  {form.errors.clientId ? <div className="evvr-err">{form.errors.clientId}</div> : null}
                </div>
                <div className="field" style={{ marginBottom: 0 }}>
                  <label>Service <span style={{ fontSize: 11, fontWeight: 500, color: "var(--app-text-muted)" }}>(optional)</span></label>
                  <ServiceMultiSelect options={serviceOptions} value={form.values.services} onChange={(vals) => form.setFieldValue("services", vals)} />
                </div>
              </div>
            ) : null}
          </div>

          {/* ---------- When ---------- */}
          <div className="cs-section">
            <div className="field" style={{ marginBottom: 0 }}>
              <label>Date <span className="req">*</span></label>
              <DateField value={form.values.date} maxDate={new Date()} onChange={(d) => form.setFieldValue("date", d)} />
              {form.errors.date ? <div className="evvr-err">{form.errors.date}</div> : null}
            </div>

            <div style={{ marginTop: 18 }}>
              <div className="time-row">
                <div className="field" style={{ margin: 0 }}>
                  <label>Clock-in <span className="req">*</span></label>
                  <input className="inp" type="time" value={form.values.startTime} onChange={(e) => form.setFieldValue("startTime", e.target.value)} />
                </div>
                <div className="field" style={{ margin: 0 }}>
                  <label>Clock-out <span className="req">*</span></label>
                  <input className="inp" type="time" value={form.values.endTime} onChange={(e) => form.setFieldValue("endTime", e.target.value)} />
                </div>
                <div>
                  <div className="dur-lbl">Duration</div>
                  <div className="dur-chip"><Icon name="clock" size={14} /><span>{duration}</span></div>
                </div>
              </div>
              {form.errors.startTime || form.errors.endTime ? <div className="evvr-err">{form.errors.startTime || form.errors.endTime}</div> : null}
            </div>
          </div>

          {/* ---------- Breaks ---------- */}
          <div className="cs-section">
            <div className="evvr-sec-row">
              <span className="type-label" style={{ margin: 0 }}>Break Times</span>
              <button type="button" className="btn btn-soft btn-sm" onClick={addBreak}><Icon name="plus" size={14} /> Add Break</button>
            </div>
            {form.values.breaks.map((_, i) => (
              <div key={i} className="evvr-break">
                <div className="evvr-break-h">
                  <span>Break {i + 1}</span>
                  <button type="button" className="evvr-break-x" onClick={() => removeBreak(i)} aria-label="Remove break"><Icon name="trash" size={14} /></button>
                </div>
                <div className="frow">
                  <div className="field" style={{ marginBottom: 0 }}>
                    <label>Break start</label>
                    <input className="inp" type="time" value={form.values.breaks[i]?.breakStart || ""} onChange={(e) => form.setFieldValue(`breaks.${i}.breakStart`, e.target.value)} />
                  </div>
                  <div className="field" style={{ marginBottom: 0 }}>
                    <label>Break end</label>
                    <input className="inp" type="time" value={form.values.breaks[i]?.breakEnd || ""} onChange={(e) => form.setFieldValue(`breaks.${i}.breakEnd`, e.target.value)} />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* ---------- Reason ---------- */}
          <div className="cs-section">
            <div className="field" style={{ marginBottom: 0 }}>
              <label>Reason for manual entry <span style={{ fontSize: 11, fontWeight: 500, color: "var(--app-text-muted)" }}>(optional)</span></label>
              <textarea
                className="inp"
                rows={3}
                value={form.values.notes}
                onChange={(e) => form.setFieldValue("notes", e.target.value)}
                placeholder="Why is this time being added manually? e.g. no shift was scheduled, staff forgot to clock out."
              />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: "var(--app-text-muted)", marginTop: 10 }}>
              <Icon name="pin" size={15} /> Saved as a manual entry — it is marked as such in timesheets and exports.
            </div>
          </div>
        </div>

        <div className="sd-foot">
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <span className="spacer" />
          <button type="button" className="btn btn-primary btn-sm" onClick={handleSubmit} disabled={loading}>
            <Icon name="check" size={16} /> Add Missed Time
          </button>
        </div>
      </div>
    </Drawer>
  );
}
